import { CommonModule } from '@angular/common'
import { Component, ContentChild, Directive, EventEmitter, Injector, Input, NgModule, OnInit, Output, TemplateRef, ViewChild, ChangeDetectorRef, AfterViewChecked, OnDestroy, HostListener } from '@angular/core'
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { Table, TableModule } from '@openng/optimus-ui/table'
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner'
import { InputGroupModule } from '@openng/optimus-ui/inputgroup'
import { InputGroupAddonModule } from '@openng/optimus-ui/inputgroupaddon'
import { ButtonModule } from '@openng/optimus-ui/button'
import { InputTextModule } from '@openng/optimus-ui/inputtext'
import { DialogModule } from '@openng/optimus-ui/dialog'
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch'
import { CheckboxModule } from '@openng/optimus-ui/checkbox'
import { RippleModule } from '@openng/optimus-ui/ripple'
import { FormsModule } from '@angular/forms'
import { SelectModule } from '@openng/optimus-ui/select'
import { MultiSelectModule } from '@openng/optimus-ui/multiselect'
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton'
import { DatePickerModule } from '@openng/optimus-ui/datepicker'
import { cloneDeep } from '../../../../app/_helper/clone.util'
import dayjs from 'dayjs'
import * as FileSaver from '../../../../app/_helper/file-saver.util'
import { echarts } from '../../../_shared/echarts-setup'
import { TooltipModule } from '@openng/optimus-ui/tooltip'

export type FqTableDensity = 'compact' | 'normal' | 'comfortable'

type PivotAgg = 'count' | 'sum' | 'avg' | 'min' | 'max'

interface PivotMeasure {
  key: string
  label: string
  aggregation: PivotAgg
  valueField: string | null
}

interface PivotNode {
  pathKey: string
  groups: string[]
  allRows: any[]
  children: PivotNode[]
  values: (number | null)[]
}

interface FqTableSavedSettings {
  columns: { field: string, show: boolean, frozen: boolean }[]
  groupRowsBy?: string
  rowGroupMode?: string
  expandableRows?: boolean
  showColFilters?: boolean
  density?: FqTableDensity
  rows?: number
  sortField?: string | null
  sortOrder?: number
}

// Cattura l'Injector del nodo <p-table> (che possiede i provider di `Table`,
// `TableService`, ...). I template proiettati dal consumatore (#header/#body/
// #captionActions) usano le direttive PrimeNG (pSortableColumn, p-tableCheckbox,
// p-columnFilter, pFrozenColumn, ...) che richiedono quei provider: senza un
// injector corretto la loro istanziazione fallirebbe con NG0201 e l'header
// proiettato non verrebbe renderizzato.
@Directive({ standalone: true, selector: '[fqTableHostInjector]' })
export class FqTableHostInjectorDirective {
  constructor (public readonly hostInjector: Injector) {}
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, InputGroupModule, InputGroupAddonModule, ButtonModule, InputTextModule, DialogModule, ToggleSwitchModule, CheckboxModule, RippleModule, SelectModule, MultiSelectModule, SelectButtonModule, DatePickerModule, ProgressSpinnerModule, TooltipModule, DragDropModule, FqTableHostInjectorDirective],
  selector: 'fq-table',
  templateUrl: './fqtable.component.html',
  styleUrls: ['./fqtable.component.css']
})
export class TableComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() set value (value: any) {
    this.tableData = Array.isArray(value) ? value : []
    this.updateRowColumns()
    this.expandAllRows()
  };

  // @deprecated Logica di dominio AWP: preferire una `exportValue` per colonna.
  @Input() useTecnologiaAgglomerataExport: boolean = false
  @Input() globalFilterFields: string[] | null = null
  @Input() selectableRows: boolean = false
  @Input() selectionMode: 'single' | 'multiple' = 'multiple'
  @Input() selectedRows: any[] = []
  @Output() selectedRowsChange = new EventEmitter<any[]>()
  @Input() showSearch: boolean = false
  @Input() showOption: boolean = true
  @Input() showStats: boolean = false
  @Input() responsiveLayout: string = 'scroll'
  @Input() styleClass: string = ''
  @Input() size: string = ''
  @Input() set rowGroupMode (value: string) {
    this._rowGroupMode = value
  }

  get rowGroupMode (): string {
    return this._rowGroupMode
  }

  @Input() set groupRowsBy (value: string) {
    this._groupRowsBy = value
  }

  get groupRowsBy (): string {
    return this._groupRowsBy
  }

  @Input() set expandableRows (value: boolean) {
    this._expandableRows = value
  }

  get expandableRows (): boolean {
    return this._expandableRows
  }

  @Input() sortField: string | null = null;
  @Input() sortMode: 'single' | 'multiple' = 'single';
  @Input() sortOrder: 1 | -1 = 1
  @Input() scrollable: boolean = true
  @Input() scrollHeight: string = 'flex'
  @Input() virtualScroll: boolean = false
  @Input() virtualScrollItemSize: number = 50

  // --- Export ---
  @Input() exportFileName: string = null
  // Formato del pulsante di export mostrato in caption: 'excel' | 'csv' | 'both'.
  @Input() exportFormat: 'excel' | 'csv' | 'both' = 'excel'
  // Esporta solo le righe correntemente filtrate. Default true (WYSIWYG).
  @Input() exportFilteredOnly: boolean = true
  // Esporta anche le colonne nascoste. Default false (solo colonne visibili).
  @Input() exportHiddenColumns: boolean = false
  // Se la tabella è filtrata, chiede se esportare solo i filtrati o tutte le righe.
  @Input() confirmExportWhenFiltered: boolean = true

  // --- Pagination ---
  @Input() paginator: boolean = false
  @Input() rows: number = 10
  @Input() rowsPerPageOptions: number[] = [10, 25, 50, 100]
  @Input() paginatorPosition: 'top' | 'bottom' | 'both' = 'bottom'
  @Input() alwaysShowPaginator: boolean = true
  @Input() showJumpToPageDropdown: boolean = false
  @Input() showCurrentPageReport: boolean = false
  @Input() currentPageReportTemplate: string = 'Showing {first} to {last} of {totalRecords}'

  // --- Selection / identity ---
  @Input() dataKey: string = ''

  // --- Look & feel ---
  @Input() stripedRows: boolean = true
  @Input() rowHover: boolean = false
  @Input() showGridlines: boolean = false

  // --- Column features ---
  @Input() resizableColumns: boolean = false
  @Input() columnResizeMode: 'fit' | 'expand' = 'fit'
  @Input() reorderableColumns: boolean = false

  // --- Filtering ---
  @Input() filterDelay: number = 300
  // Mostra il pulsante che apre/chiude la riga dei filtri per colonna nella caption.
  @Input() showColumnToggle: boolean = true
  // Forza/imbisce la toolbar in caption. Se null (default) la toolbar compare
  // quando c'è la ricerca, un'azione proiettata, le statistiche o l'export.
  @Input() showToolbar: boolean | null = null

  // --- Events passthrough ---
  @Output() rowSelect = new EventEmitter<any>()
  @Output() rowUnselect = new EventEmitter<any>()
  @Output() pageChange = new EventEmitter<any>()
  @Output() sortChange = new EventEmitter<any>()
  @Output() filterChange = new EventEmitter<any>()
  @Output() lazyLoad = new EventEmitter<any>()
  @Output() colResize = new EventEmitter<any>()
  @Output() colReorder = new EventEmitter<any>()
  @Output() headerCheckboxToggle = new EventEmitter<any>()

  @Input() set columns (value: any[]) {
    this._columns = Array.isArray(value) ? value.map(col => ({
      ...col,
      show: col.show !== undefined ? col.show : true,
      frozen: col.frozen === true // forza boolean, default false
    })) : []
    // Conserva la definizione "di fabbrica" delle colonne per poter ripristinare
    // i default dal pulsante Reset (le assegnazioni interne non la sovrascrivono).
    if (!this.columnsSetInternally) {
      this.originalColumns = cloneDeep(this._columns)
    }
    // Le impostazioni salvate dipendono dall'elenco dei campi: vanno (ri)caricate
    // qui, dopo che `_columns` è noto. Caricarle solo nel setter di `tableId`
    // non funzionava perché a quel punto `_columns` è ancora vuoto e la chiave
    // di storage risultava senza campi, quindi nessun salvataggio trovato.
    this.savedSettings = this.loadSettings()
    this.applySavedSettings()
    this.columnsCopy = cloneDeep(this._columns)
    this.updateVisibleColumns()
    this.updateRowColumns()
  }

  get columns (): any[] {
    return this._columns
  }

  @Input() set tableId (newKey: any) {
    if (this.prevKey !== newKey) {
      this.expandAllRows()
      this.showColFilters = false // reset column filters visibility when key changes
      this.prevKey = newKey
      this.searchTerm = null
      if (this.pt) {
        this.pt.filterGlobal(null, 'contains') // reset the global filter when key changes
        this.searchInput?.nativeElement && (this.searchInput.nativeElement.value = '') // reset the search input field
        if (this.searchInput?.value !== undefined) {
          this.searchInput.value = '' // reset the search input value
        }
        for (const col of this.columns) {
          this.pt.filter(null, col.field, 'contains')
        }
      }
      this.restoreSettings()
    }
  }

  @Input() set settingsKey (value: string | null) {
    if (this._settingsKey !== value) {
      this._settingsKey = value
      this.restoreSettings()
    }
  }

  get settingsKey (): string | null {
    return this._settingsKey
  }

  private _settingsKey: string | null = null
  private savedSettings: FqTableSavedSettings | null = null

  rowColumns: any[] = []
  disableAutocomplete = true
  tableData: any[] = []
  private _columns: any[] = []
  private originalColumns: any[] = []
  private columnsSetInternally = false
  private _rowGroupMode: string = ''
  private _groupRowsBy: string = ''
  private _expandableRows: boolean = false
  private groupByFieldValue: string = '' // traccia quale colonna è nascosta per il groupby
  private originalGroupByShowValue: boolean = true // salva il valore originale di show
  cachedVisibleColumns: any[] = [] // cachato per evitare problemi con change detection
  private frozenCountCache: number = 0
  private globalFilterFieldsCache: string[] = []
  private pivotFieldOptionsCache: Array<{ label: string, value: string }> = []
  columnsCopy: any[] = []
  previewDensity: FqTableDensity = 'normal'
  density: FqTableDensity = 'normal'
  readonly densityOptions: Array<{ label: string, value: FqTableDensity }> = [
    { label: 'Compact', value: 'compact' },
    { label: 'Normal', value: 'normal' },
    { label: 'Comfortable', value: 'comfortable' }
  ]
  prevKey: any = null
  searchTerm: string | null = null
  /** Riga dei filtri di colonna: attivabile anche dall'esterno. */
  @Input() showColFilters: boolean = false
  expandedRowGroups: { [key: string]: boolean } = {}
  openOptions = false
  // Export scope confirmation dialog (shown only when the table is filtered).
  exportDialogVisible = false
  pendingExportFormat: 'excel' | 'csv' = 'excel'
  private _openStats = false
  get openStats (): boolean {
    return this._openStats
  }

  set openStats (v: boolean) {
    if (!v) {
      this.disposeChart()
      this.pivotShowChart = false
    }
    this._openStats = v
  }

@Input() loading = false
  tableStatistics: any = null
  @ViewChild('pt') pt: Table
  // Injector del nodo <p-table>: serve a far risolvere le direttive PrimeNG
  // (pSortableColumn, p-tableCheckbox, p-columnFilter, pFrozenColumn, ...) usate
  // nei template proiettati (#header/#body/#captionActions), che altrimenti
  // cercherebbero i provider (Table, TableService, ...) nell'injector del
  // componente consumatore e fallirebbero con NG0201.
  @ViewChild(FqTableHostInjectorDirective, { static: true }) hostInjectorDir: FqTableHostInjectorDirective
  get ptInjector (): Injector { return this.hostInjectorDir?.hostInjector }
  @ViewChild('searchInput') searchInput: any
  @ContentChild('header', { static: false }) headerTemplate: TemplateRef<any>
  @ContentChild('body', { static: false }) bodyTemplate: TemplateRef<any>
  @ContentChild('footer', { static: false }) footerTemplate: TemplateRef<any>
  // Slot libero proiettato a sinistra nella caption, sulla stessa riga della ricerca.
  // Può contenere bottoni, testo o qualsiasi contenuto.
  @ContentChild('captionActions', { static: false }) captionActionsTemplate: TemplateRef<any>
  // Messaggio mostrato quando la tabella non ha righe: personalizzabile via
  // `[emptyMessage]` oppure proiettando un template `#emptymessage`.
  @ContentChild('emptymessage', { static: false }) emptyTemplate: TemplateRef<any>
  @Input() emptyMessage: string = 'No items found'
  @Input() loadingMessage: string = 'Loading data...'

  /**
   * Filtro globale impostabile dall'esterno (es. prefill da link ?fraz=):
   * applica `filterGlobal(value, 'contains')` alla tabella.
   */
  @Input() set globalFilter (value: string | null | undefined) {
    this.pendingGlobalFilter = (value || '').trim() || null
  }

  private pendingGlobalFilter: string | null = null
  private appliedGlobalFilter: string | null = null

  private syncGlobalFilter (): void {
    if (!this.pt || this.pendingGlobalFilter === this.appliedGlobalFilter) {
      return
    }
    this.appliedGlobalFilter = this.pendingGlobalFilter
    const value = this.appliedGlobalFilter
    setTimeout(() => {
      try {
        this.pt?.filterGlobal(value, 'contains')
      } catch { /* tabella non ancora pronta */ }
    }, 0)
  }

  constructor (private readonly cdr: ChangeDetectorRef) {}

  ngOnInit () {
  }

  get frozenCount (): number {
    return this.frozenCountCache
  }

  // Classi del p-datatable: mantengono lo styleClass esterno, aggiungono la densità
  // e (solo in virtual scroll) la classe che applica l'altezza riga pari all'itemSize.
  get tableStyleClass (): string {
    return `${this.styleClass || ''} fq-density-${this.density}${this.virtualScroll ? ' fq-virtual' : ''}`.trim()
  }

  // Con il virtual scroll l'altezza riga deve combaciare con l'itemSize dello scroller,
  // altrimenti lo scrolling risulta sfalsato. La densità quindi regola anche l'itemSize.
  get effectiveVirtualScrollItemSize (): number {
    if (this.density === 'compact') {
      return 28
    }
    if (this.density === 'comfortable') {
      return 44
    }
    return this.virtualScrollItemSize
  }

  // Altezza riga applicata via CSS var (usata solo in virtual scroll).
  get virtualRowHeight (): string | null {
    return this.virtualScroll ? this.effectiveVirtualScrollItemSize + 'px' : null
  }

  private frozenSyncKey = ''
  private frozenRaf: number | null = null
  private frozenResize: ResizeObserver | null = null
  private frozenObservedHeader: HTMLElement | null = null
  private frozenMutation: MutationObserver | null = null
  private frozenObservedTbody: HTMLElement | null = null

  ngAfterViewChecked () {
    this.syncGlobalFilter()
    if (this.frozenCount === 0) {
      if (this.frozenSyncKey !== '' && this.clearFrozenColumns()) {
        this.frozenSyncKey = ''
      }
      this.disconnectFrozenObservers()
      return
    }
    const key = `${this.frozenCount}|${this.tableData.length}|${this.cachedVisibleColumns.length}`
    if (key !== this.frozenSyncKey) {
      this.frozenSyncKey = key
      this.syncFrozenColumns()
    }
  }

  @HostListener('window:resize')
  onResize () {
    if (this.frozenCount > 0) {
      this.syncFrozenColumns()
    }
    if (this.pivotChartInstance) {
      this.pivotChartInstance.resize()
    }
  }

  ngOnDestroy () {
    if (this.frozenRaf) {
      cancelAnimationFrame(this.frozenRaf)
      this.frozenRaf = null
    }
    this.disconnectFrozenObservers()
    this.disposeChart()
  }

  private disconnectFrozenObservers () {
    if (this.frozenResize) {
      this.frozenResize.disconnect()
      this.frozenResize = null
      this.frozenObservedHeader = null
    }
    if (this.frozenMutation) {
      this.frozenMutation.disconnect()
      this.frozenMutation = null
      this.frozenObservedTbody = null
    }
  }

  // Le colonne bloccate (Lock) vengono gelate applicando position:sticky + left alla
  // cella il cui campo ha frozen===true, mantenendo l'ordine originale delle colonne.
  // L'offset `left` è la posizione "naturale" della colonna (somma delle larghezze di
  // tutte le colonne precedenti), esattamente come fa la direttiva pFrozenColumn
  // sull'header: così header e body restano perfettamente allineati.
  // Poiché PrimeNG ricrea le righe del body ad ogni render/sort/filter, lo stile viene
  // riapplicato tramite un MutationObserver sul tbody e gli offset ricalcolati al variare
  // delle larghezze.
  private syncFrozenColumns () {
    const tableEl: HTMLElement = this.pt?.el?.nativeElement
    if (!tableEl || this.frozenCount === 0) {
      return
    }
    const columns = this.visibleColumns
    const rows = tableEl.querySelectorAll('.p-datatable-tbody > tr')
    rows.forEach((row) => {
      // salta la riga di raggruppamento (td con colspan) e le righe di dettaglio custom
      if ((row as HTMLElement).querySelector('td[colspan]')) {
        return
      }
      const cells = Array.from(row.querySelectorAll('td')) as HTMLElement[]
      cells.forEach((c: HTMLElement) => {
        c.classList.remove('p-datatable-frozen-column')
        c.style.position = ''
        c.style.left = ''
        c.style.zIndex = ''
      })
      // Riapplica sticky solo sulle celle la cui colonna è frozen. L'offset `left`
      // viene calcolato in un secondo momento (scheduleFrozenOffsets) quando le
      // larghezze dell'header sono disponibili.
      for (let i = 0; i < cells.length && i < columns.length; i++) {
        const cell = cells[i]
        if (columns[i] && columns[i].frozen === true) {
          cell.classList.add('p-datatable-frozen-column')
          cell.style.position = 'sticky'
          // z-index 2 resta comunque sotto l'header sticky (z-index 3) ma sopra
          // le altre celle frozen (z-index 1): serve alle celle con popup/overlay
          // (es. speeddial) che altrimenti verrebbero coperte dalla colonna frozen successiva.
          cell.style.zIndex = String(columns[i].frozenZIndex ?? 1)
        }
      }
    })
    this.ensureFrozenObservers(tableEl)
    this.scheduleFrozenOffsets(tableEl)
  }

  // Rimuove lo stile di freeze da tutte le celle (header e body).
  // Ritorna false se la tabella non è ancora disponibile.
  private clearFrozenColumns (): boolean {
    const tableEl = this.pt?.el?.nativeElement
    if (!tableEl) {
      return false
    }
    const rows = tableEl.querySelectorAll('.p-datatable-thead > tr, .p-datatable-tbody > tr')
    rows.forEach((row: HTMLElement) => {
      const cells = row.querySelectorAll('th, td')
      const cellList = Array.from(cells) as HTMLElement[]
      cellList.forEach((c: HTMLElement) => {
        c.classList.remove('p-datatable-frozen-column')
        c.style.position = ''
        c.style.left = ''
        c.style.zIndex = ''
      })
    })
    return true
  }

  // Calcola gli offset `left` dopo il layout, così le larghezze delle celle
  // sono già note. Usa le larghezze delle celle dell'header come riferimento
  // Ricalcola gli offset `left` DOPO il layout, usando le larghezze delle celle
  // dell'header (come fa la direttiva pFrozenColumn sull'header) per restare allineato.
  private scheduleFrozenOffsets (tableEl: HTMLElement) {
    if (this.frozenRaf) {
      cancelAnimationFrame(this.frozenRaf)
    }
    this.frozenRaf = requestAnimationFrame(() => {
      this.frozenRaf = null
      const columns = this.visibleColumns
      const headerRow = tableEl.querySelector('.p-datatable-thead > tr')
      const headerCells = headerRow ? (Array.from(headerRow.querySelectorAll('th')) as HTMLElement[]) : []
      const rows = tableEl.querySelectorAll('.p-datatable-tbody > tr')
      rows.forEach((row) => {
        if ((row as HTMLElement).querySelector('td[colspan]')) {
          return
        }
        const cells = Array.from(row.querySelectorAll('td')) as HTMLElement[]
        let acc = 0
        for (let i = 0; i < cells.length && i < columns.length; i++) {
          if (columns[i] && columns[i].frozen === true) {
            cells[i].style.left = acc + 'px'
          }
          acc += headerCells[i] ? headerCells[i].offsetWidth : (cells[i]?.offsetWidth || 0)
        }
      })
    })
  }

  // Rende lo stile frozen durabile:
  //  - MutationObserver sul tbody: se PrimeNG ricrea le righe (sort/filter/scroll virtuale/refresh),
  //    lo stile viene riapplicato.
  //  - ResizeObserver sulla riga di header: se le larghezze delle colonne cambiano (resize, font,
  //    scrollbar), gli offset `left` vengono ricalcolati.
  private ensureFrozenObservers (tableEl: HTMLElement) {
    const tbody = tableEl.querySelector('.p-datatable-tbody') as HTMLElement | null
    const thead = tableEl.querySelector('.p-datatable-thead') as HTMLElement | null
    if (!tbody) {
      return
    }
    if (this.frozenObservedTbody !== tbody) {
      if (this.frozenMutation) {
        this.frozenMutation.disconnect()
      }
      this.frozenObservedTbody = tbody
      this.frozenMutation = new MutationObserver(() => {
        // le righe sono state ricreate: riapplica lo stile frozen
        this.syncFrozenColumns()
      })
      // solo childList/subtree: le nostre modifiche agli stili non ritriggerano l'observer
      this.frozenMutation.observe(tbody, { childList: true, subtree: true })
    }
    const headerRow = thead ? (thead.querySelector('tr') as HTMLElement | null) : null
    if (headerRow && this.frozenObservedHeader !== headerRow) {
      if (this.frozenResize) {
        this.frozenResize.disconnect()
      }
      this.frozenObservedHeader = headerRow
      this.frozenResize = new ResizeObserver(() => {
        if (this.frozenCount > 0) {
          this.scheduleFrozenOffsets(tableEl)
        }
      })
      this.frozenResize.observe(headerRow)
    }
  }

  updateRowColumns () {
    // Popola rowColumns usando columns se disponibili, altrimenti genera dai dati
    if (this.columns && this.columns.length > 0) {
      // Se columns è stato definito, usalo
      this.rowColumns = cloneDeep(this.columns)
    } else if (this.tableData && this.tableData.length > 0) {
      // Altrimenti genera dai dati
      this.rowColumns = Object.keys(this.tableData[0]).map(key => {
        return { field: key, header: key, show: true }
      })
    }
    this.rebuildGlobalFilterFieldsCache()
    this.filterOptionsCache.clear()
    this.pivotFieldOptionsCache = (this.columns || [])
      .filter(col => !!col.field)
      .map(col => ({ label: col.header || col.field, value: col.field }))
  }

  private rebuildGlobalFilterFieldsCache () {
    if (this.globalFilterFields && this.globalFilterFields.length) {
      this.globalFilterFieldsCache = this.globalFilterFields
      return
    }
    if (this.tableData && this.tableData.length) {
      const keys = Object.keys(this.tableData[0])
      this.globalFilterFieldsCache = (this.columns && this.columns.length)
        ? keys.filter(key => this.columns.some(col => col.field === key))
        : keys
      return
    }
    this.globalFilterFieldsCache = []
  }

  // Assegna le colonne senza trattarle come input esterno: serve a non
  // sovrascrivere la definizione "di fabbrica" usata dal Reset.
  private setColumnsInternal (cols: any[]) {
    this.columnsSetInternally = true
    this.columns = cols
    this.columnsSetInternally = false
  }

  onSelectionChange (selection: any[]) {
    this.selectedRows = selection
    this.selectedRowsChange.emit(selection)
  }

  // Intercetta l'ordinamento per persistere field/ordine e ri-esporre l'evento.
  onSortInternal (event: any) {
    if (event && event.field !== undefined) {
      this.sortField = event.field
      this.sortOrder = event.order
      this.persistSettings()
    }
    this.sortChange.emit(event)
  }

  onGroupByChange (fieldValue: string) {
    // Aggiorna groupRowsBy
    this.loading = true
    setTimeout(() => {
      // Se c'era una colonna nascosta precedentemente, ripristinala
      if (this.groupByFieldValue && this.groupByFieldValue !== fieldValue) {
        this.setColumnsInternal(this.columns.map(col => {
          if (col.field === this.groupByFieldValue) {
            return { ...col, show: this.originalGroupByShowValue }
          }
          return col
        }))
      }

      this._groupRowsBy = fieldValue

      // Se una colonna è selezionata, attiva il raggruppamento
      if (fieldValue && fieldValue.trim() !== '') {
        this._rowGroupMode = 'subheader'
        this._expandableRows = true

        // Nascondi la colonna selezionata per il raggruppamento - ricrea l'array
        this.setColumnsInternal(this.columns.map(col => {
          if (col.field === fieldValue) {
            this.originalGroupByShowValue = col.show !== false
            return { ...col, show: false }
          }
          return col
        }))
        this.groupByFieldValue = fieldValue

        // Sincronizza columnsCopy con columns
        this.columnsCopy = cloneDeep(this.columns)
        this.updateVisibleColumns()

        // Ricrea la tabella azzerando e ricevendendo i dati per forzare il refresh
        const tempData = cloneDeep(this.tableData)
        this.tableData = []
        this.cdr.detectChanges()
        setTimeout(() => {
          this.tableData = tempData
          this.expandAllRows()
          this.cdr.detectChanges()
        }, 50)
      } else {
      // Se deselezionato, disattiva il raggruppamento e ripristina la colonna
        this._rowGroupMode = ''
        this._expandableRows = false
        this.expandedRowGroups = {}

        // Ripristina la colonna precedentemente nascosta - ricrea l'array
        if (this.groupByFieldValue) {
          this.setColumnsInternal(this.columns.map(col => {
            if (col.field === this.groupByFieldValue) {
              return { ...col, show: this.originalGroupByShowValue }
            }
            return col
          }))
        }
        this.groupByFieldValue = ''

        // Sincronizza columnsCopy con columns
        this.columnsCopy = cloneDeep(this.columns)
        this.updateVisibleColumns()

        // Ricrea la tabella azzerando e ricevendendo i dati per forzare il refresh
        const tempData = cloneDeep(this.tableData)
        this.tableData = []
        this.cdr.detectChanges()
        setTimeout(() => {
          this.tableData = tempData
          this.cdr.detectChanges()
        }, 50)
      }
      this.loading = false
      this.persistSettings()
    }, 0)
  }

  toggleColFilters () {
    this.showColFilters = !this.showColFilters
    this.disableAutocomplete = true
    this.persistSettings()
    setTimeout(() => this.syncRowGroupHeaderOffset(), 0)
  }

  // PrimeNG ricalcola l'offset sticky degli header di gruppo solo quando cambia `value`,
  // quindi mostrare/nascondere la riga dei filtri lascia in giro l'altezza vecchia del thead.
  private syncRowGroupHeaderOffset () {
    const table = this.pt as any
    if (!table?.el?.nativeElement || this.rowGroupMode !== 'subheader') return
    const thead = table.el.nativeElement.querySelector('.p-datatable-thead')
    if (thead) {
      table.rowGroupHeaderStyleObject.top = thead.offsetHeight + 'px'
      this.cdr.detectChanges()
    }
  }

  // --- Custom Pivot / Custom Analysis state ---
  statsView: 'overview' | 'pivot' = 'overview'
  pivotMode: 'grouped' | 'crosstab' = 'grouped'

  // Grouped
  pivotGroupBy: string[] = []
  pivotAggregations: PivotAgg[] = ['count']
  pivotSortBy: 'label' | 'value' = 'label'
  pivotSortOrder: 'asc' | 'desc' = 'asc'
  pivotHavingOperator: '>=' | '<=' | '=' | 'between' = '>='
  pivotMinValue: number = null
  pivotMaxValue: number = null

  // Cross-tab
  pivotRowField: string = null
  pivotColField: string = null
  pivotMeasure: 'value' | 'rowPct' | 'colPct' | 'totalPct' = 'value'
  pivotTopColumns: number = 30
  pivotGroupOthers: boolean = true

  // Shared
  pivotValueField: string = null
  pivotDateBucket: 'none' | 'year' | 'month' | 'day' = 'none'
  pivotNumericInterval: number = null
  pivotUseFiltered: boolean = true
  pivotCompactNumbers: boolean = false
  pivotLoading: boolean = false
  pivotDrillDownKeepOpen: boolean = false
  private pivotDebounce: any = null

  // Results
  pivotResult: any[] = []
  pivotColumns: Array<{ field: string, header: string }> = []
  pivotMeasures: PivotMeasure[] = []
  pivotGrandTotals: number[] = []
  pivotShowPercentage: boolean = true
  pivotTruncatedColumns: number = 0
  pivotCrossTab: {
    rowFields: string[]
    colFields: string[]
    cells: (number | null)[][]
    rowTotals: (number | null)[]
    colTotals: (number | null)[]
    grandTotal: number | null
    hasOthers: boolean
  } = null
  pivotExpandedKeys = new Set<string>()
  pivotAppliedFilters: Array<{ field: string, value: any }> = []
  private pivotTree: PivotNode[] = []

  // Chart
  pivotChartOptions: any = null
  pivotShowChart: boolean = false
  pivotChartType: 'bar' | 'line' | 'pie' = 'bar'
  pivotChartTopN: number = 20
  pivotChartLogScale: boolean = false
  pivotChartDataLabels: boolean = false
  pivotChartPercent: boolean = false

  @ViewChild('pivotChart') pivotChartEl: any
  private pivotChartInstance: any = null
  private readonly pivotPalette = ['#19549f', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#2b897a', '#ca8a04', '#dc2626']

  readonly statsViewOptions = [
    { label: 'Statistical Overview', value: 'overview', icon: 'pi pi-chart-bar' },
    { label: 'Custom Analysis', value: 'pivot', icon: 'pi pi-sitemap' }
  ]

  readonly pivotModeOptions = [
    { label: 'Grouped', value: 'grouped', icon: 'pi pi-list' },
    { label: 'Cross-tab', value: 'crosstab', icon: 'pi pi-table' }
  ]

  readonly pivotAggregationOptions: Array<{ label: string, value: PivotAgg }> = [
    { label: 'Count of rows', value: 'count' },
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Min', value: 'min' },
    { label: 'Max', value: 'max' }
  ]

  readonly pivotSortOptions = [
    { label: 'Label', value: 'label' },
    { label: 'Value', value: 'value' }
  ]

  readonly pivotSortOrderOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' }
  ]

  readonly pivotHavingOptions = [
    { label: '>=', value: '>=' },
    { label: '<=', value: '<=' },
    { label: '=', value: '=' },
    { label: 'Between', value: 'between' }
  ]

  readonly pivotMeasureOptions = [
    { label: 'Absolute value', value: 'value' },
    { label: '% of row', value: 'rowPct' },
    { label: '% of column', value: 'colPct' },
    { label: '% of total', value: 'totalPct' }
  ]

  readonly pivotDateBucketOptions = [
    { label: 'None', value: 'none' },
    { label: 'Year', value: 'year' },
    { label: 'Month', value: 'month' },
    { label: 'Day', value: 'day' }
  ]

  private readonly _chartTypesGrouped: Array<{ label: string, value: string }> = [
    { label: 'Bar', value: 'bar' }, { label: 'Line', value: 'line' }, { label: 'Pie', value: 'pie' }
  ]

  private readonly _chartTypesCrosstab: Array<{ label: string, value: string }> = [
    { label: 'Bar', value: 'bar' }, { label: 'Line', value: 'line' }
  ]

  get pivotChartTypeOptions (): Array<{ label: string, value: string }> {
    return this.pivotMode === 'crosstab' ? this._chartTypesCrosstab : this._chartTypesGrouped
  }

  // Colonne selezionabili per l'analisi: esclude le pseudo-colonne (action) senza field.
  get pivotFieldOptions (): Array<{ label: string, value: string }> {
    return this.pivotFieldOptionsCache
  }

  get pivotGroupByOptions (): Array<{ label: string, value: string }> {
    return this.pivotFieldOptions
  }

  get pivotValueFieldOptions (): Array<{ label: string, value: string }> {
    return this.pivotFieldOptions
  }

  // Numero di colonne di raggruppamento (per distinguere celle gruppo/misura).
  get pivotGroupCount (): number {
    return this.pivotMode === 'grouped' ? this.pivotGroupBy.filter(f => !!f).length : 0
  }

  // Aggregazione singola usata dal cross-tab (il grouped usa l'array completo).
  get pivotCrosstabAgg (): PivotAgg {
    return this.pivotAggregations[0] || 'count'
  }

  set pivotCrosstabAgg (value: PivotAgg) {
    this.pivotAggregations = [value]
  }

  get pivotHasFilteredRows (): boolean {
    return !!(this.pt?.hasFilter && this.pt.hasFilter())
  }

  // Righe su cui gira l'analisi: filtrate (default) o tutte, in base al toggle.
  get pivotSourceRows (): any[] {
    if (this.pivotUseFiltered && this.pivotHasFilteredRows) {
      return this.pt?.filteredValue || []
    }
    return this.tableData
  }

  get pivotIsFiltered (): boolean {
    return this.pivotSourceRows.length < (this.tableData?.length ?? 0)
  }

  get pivotSourceCount (): number {
    return this.pivotSourceRows.length
  }

  headerOf (field: string): string {
    const col = this.columns.find(c => c.field === field)
    return col ? col.header : field
  }

  private cap (text: string): string {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
  }

  private measureLabel (measure: PivotMeasure): string {
    if (measure.aggregation === 'count') {
      return 'Count'
    }
    return `${this.cap(measure.aggregation)} of ${this.headerOf(measure.valueField)}`
  }

  private buildMeasures (): void {
    const selected = (this.pivotAggregations && this.pivotAggregations.length) ? this.pivotAggregations : ['count'] as PivotAgg[]
    let valid = selected.filter(agg => agg === 'count' || !!this.pivotValueField)
    if (!valid.length) {
      valid = ['count'] as PivotAgg[]
    }
    this.pivotMeasures = valid.map(agg => {
      const measure: PivotMeasure = {
        key: agg + (agg === 'count' ? '' : '_' + this.pivotValueField),
        label: '',
        aggregation: agg,
        valueField: this.pivotValueField
      }
      measure.label = this.measureLabel(measure)
      return measure
    })
  }

  // Valore di raggruppamento con bucket temporale/numerico opzionale.
  private groupValue (row: any, field: string): string {
    const raw = row ? row[field] : null
    if (raw === null || raw === undefined || raw === '') {
      return '(empty)'
    }
    if (this.pivotDateBucket !== 'none') {
      const date = dayjs(raw)
      if (date.isValid()) {
        if (this.pivotDateBucket === 'year') {
          return date.format('YYYY')
        }
        if (this.pivotDateBucket === 'month') {
          return date.format('YYYY-MM')
        }
        if (this.pivotDateBucket === 'day') {
          return date.format('YYYY-MM-DD')
        }
      }
    }
    const interval = Number(this.pivotNumericInterval)
    if (!isNaN(interval) && interval > 0) {
      const num = parseFloat(String(raw))
      if (!isNaN(num)) {
        const low = Math.floor(num / interval) * interval
        return `${low} - ${low + interval}`
      }
    }
    return String(raw)
  }

  private passesHaving (value: number | null): boolean {
    if (this.pivotHavingOperator === 'between') {
      if (this.pivotMinValue == null && this.pivotMaxValue == null) {
        return true
      }
      if (value == null) {
        return false
      }
      if (this.pivotMinValue != null && value < Number(this.pivotMinValue)) {
        return false
      }
      if (this.pivotMaxValue != null && value > Number(this.pivotMaxValue)) {
        return false
      }
      return true
    }
    if (this.pivotMinValue == null) {
      return true
    }
    if (value == null) {
      return false
    }
    const threshold = Number(this.pivotMinValue)
    if (this.pivotHavingOperator === '>=') {
      return value >= threshold
    }
    if (this.pivotHavingOperator === '<=') {
      return value <= threshold
    }
    if (this.pivotHavingOperator === '=') {
      return value === threshold
    }
    return true
  }

  private compactNumber (value: number): string {
    const abs = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    if (abs >= 1e9) {
      return sign + (abs / 1e9).toFixed(1) + 'B'
    }
    if (abs >= 1e6) {
      return sign + (abs / 1e6).toFixed(1) + 'M'
    }
    if (abs >= 1e3) {
      return sign + (abs / 1e3).toFixed(1) + 'K'
    }
    return value.toLocaleString('it-IT', { maximumFractionDigits: 2 })
  }

  // Formatta i numeri dei risultati pivot con il locale italiano (o compatti K/M/B).
  formatPivotValue (value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-'
    }
    if (typeof value !== 'number') {
      return String(value)
    }
    if (this.pivotCompactNumbers) {
      return this.compactNumber(value)
    }
    const decimals = this.pivotMeasures[0]?.aggregation === 'count' ? 0 : 2
    return value.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }

  formatPivotPercent (value: number | null, base: number | null): string {
    if (value == null || !base) {
      return '-'
    }
    return ((value / base) * 100).toFixed(1) + '%'
  }

  // Testo di una cella cross-tab in base alla misura scelta (assoluto o %).
  crossTabCellDisplay (ri: number, ci: number): string {
    const cross = this.pivotCrossTab
    if (!cross) {
      return '-'
    }
    const raw = cross.cells[ri][ci]
    if (this.pivotMeasure === 'value') {
      return this.formatPivotValue(raw)
    }
    const base = this.pivotMeasure === 'rowPct'
      ? cross.rowTotals[ri]
      : this.pivotMeasure === 'colPct'
        ? cross.colTotals[ci]
        : cross.grandTotal
    return this.formatPivotPercent(raw, base)
  }

  private aggregateFor (rows: any[], measure: PivotMeasure): number | null {
    if (measure.aggregation === 'count' || !measure.valueField) {
      return rows.length
    }
    const numericValues = rows
      .map(r => parseFloat(r[measure.valueField]))
      .filter(v => !isNaN(v))
    if (numericValues.length === 0) {
      return null
    }
    if (measure.aggregation === 'sum') {
      return numericValues.reduce((a, b) => a + b, 0)
    }
    if (measure.aggregation === 'avg') {
      return numericValues.reduce((a, b) => a + b, 0) / numericValues.length
    }
    if (measure.aggregation === 'min') {
      return Math.min(...numericValues)
    }
    if (measure.aggregation === 'max') {
      return Math.max(...numericValues)
    }
    return null
  }

  private push (map: Map<string, any[]>, key: string, row: any) {
    const bucket = map.get(key)
    if (bucket) {
      bucket.push(row)
    } else {
      map.set(key, [row])
    }
  }

  // Ricalcolo differito: mostra lo spinner e lascia respirare la UI su dataset grandi.
  computePivot () {
    if (this.pivotDebounce) {
      clearTimeout(this.pivotDebounce)
    }
    this.pivotLoading = true
    this.cdr.detectChanges()
    this.pivotDebounce = setTimeout(() => {
      this.pivotDebounce = null
      this.doComputePivot()
      this.pivotLoading = false
      this.persistPivotConfig()
      this.cdr.detectChanges()
    }, 0)
  }

  private doComputePivot () {
    this.buildMeasures()
    if (this.pivotMode === 'crosstab' && this.pivotChartType === 'pie') {
      this.pivotChartType = 'bar'
    }
    if (this.pivotMode === 'grouped') {
      this.computeGrouped()
    } else {
      this.computeCrossTab()
    }
    const hasResult = this.hasPivotResult()
    if (this.pivotShowChart && hasResult) {
      this.buildChart()
      setTimeout(() => this.renderPivotChart(), 0)
    } else if (!hasResult) {
      this.disposeChart()
      this.pivotShowChart = false
    }
  }

  // Richiamato dai controlli del pivot: aggiorna subito i risultati senza "Analyze".
  onPivotChange () {
    this.computePivot()
  }

  onStatsViewChange () {
    if (this.statsView === 'pivot') {
      this.computePivot()
    }
  }

  hasPivotResult (): boolean {
    return this.pivotMode === 'grouped' ? this.pivotResult.length > 0 : !!this.pivotCrossTab
  }

  private computeGrouped () {
    this.pivotTruncatedColumns = 0
    const fields = this.pivotGroupBy.filter(f => !!f)
    this.pivotColumns = [
      ...fields.map(f => ({ field: f, header: this.headerOf(f) })),
      ...this.pivotMeasures.map((m, i) => ({ field: '__v' + i, header: m.label }))
    ]
    if (!fields.length) {
      this.pivotResult = []
      this.pivotTree = []
      this.pivotGrandTotals = []
      return
    }

    const source = this.pivotSourceRows
    const separator = '§'

    // Gruppi foglia (chiave completa).
    const leaves = new Map<string, any[]>()
    for (const row of source) {
      const key = fields.map(f => this.groupValue(row, f)).join(separator)
      this.push(leaves, key, row)
    }

    // Albero gerarchico (supporta groupBy multiplo con subtotali espandibili).
    const roots: PivotNode[] = []
    leaves.forEach((rows, key) => {
      const parts = key.split(separator)
      let level = roots
      let path: string[] = []
      for (const part of parts) {
        path = [...path, part]
        const pathKey = path.join(separator)
        let node = level.find(n => n.pathKey === pathKey)
        if (!node) {
          node = { pathKey, groups: [...path], allRows: [], children: [], values: [] }
          level.push(node)
        }
        if (path.length === parts.length) {
          node.allRows = rows
        }
        level = node.children
      }
    })

    // Aggregazione bottom-up.
    const compute = (node: PivotNode) => {
      if (node.children.length) {
        node.children.forEach(compute)
        node.allRows = node.children.flatMap(c => c.allRows)
      }
      node.values = this.pivotMeasures.map(m => this.aggregateFor(node.allRows, m))
    }
    roots.forEach(compute)

    // Having sui gruppi foglia; i padri senza figli superstiti vengono rimossi.
    const prune = (node: PivotNode): boolean => {
      if (node.children.length) {
        node.children = node.children.filter(prune)
        node.allRows = node.children.flatMap(c => c.allRows)
        node.values = this.pivotMeasures.map(m => this.aggregateFor(node.allRows, m))
        return node.children.length > 0
      }
      return this.passesHaving(node.values[0])
    }
    const finalRoots = roots.filter(prune)

    // Ordinamento ricorsivo per etichetta o valore.
    const sortNodes = (nodes: PivotNode[]) => {
      nodes.sort((a, b) => {
        let cmp: number
        if (this.pivotSortBy === 'value') {
          cmp = (a.values[0] ?? 0) - (b.values[0] ?? 0)
        } else {
          cmp = String(a.groups[a.groups.length - 1]).localeCompare(String(b.groups[b.groups.length - 1]))
        }
        if (cmp === 0) {
          cmp = a.pathKey.localeCompare(b.pathKey)
        }
        return this.pivotSortOrder === 'desc' ? -cmp : cmp
      })
      nodes.forEach(n => sortNodes(n.children))
    }
    sortNodes(finalRoots)
    this.pivotTree = finalRoots

    this.pivotGrandTotals = this.pivotMeasures.map(m => this.aggregateFor(source, m))
    this.pivotShowPercentage = this.pivotMeasures.length === 1 &&
      (this.pivotMeasures[0].aggregation === 'count' || this.pivotMeasures[0].aggregation === 'sum')

    // Prima analisi: espandi tutto.
    if (this.pivotExpandedKeys.size === 0) {
      finalRoots.forEach(n => this.collectExpandableKeys(n))
    }
    this.refreshPivotRows()
  }

  private collectExpandableKeys (node: PivotNode) {
    if (node.children.length) {
      this.pivotExpandedKeys.add(node.pathKey)
      node.children.forEach(c => this.collectExpandableKeys(c))
    }
  }

  private refreshPivotRows () {
    const out: any[] = []
    const walk = (nodes: PivotNode[], level: number) => {
      for (const node of nodes) {
        const hasChildren = node.children.length > 0
        const expanded = this.pivotExpandedKeys.has(node.pathKey)
        out.push({
          __level: level,
          __hasChildren: hasChildren,
          __expanded: expanded,
          __pathKey: node.pathKey,
          __groups: node.groups,
          __values: node.values
        })
        if (hasChildren && expanded) {
          walk(node.children, level + 1)
        }
      }
    }
    walk(this.pivotTree, 0)
    this.pivotResult = out
  }

  togglePivotNode (row: any) {
    if (!row?.__hasChildren) {
      return
    }
    if (this.pivotExpandedKeys.has(row.__pathKey)) {
      this.pivotExpandedKeys.delete(row.__pathKey)
    } else {
      this.pivotExpandedKeys.add(row.__pathKey)
    }
    this.refreshPivotRows()
    this.cdr.detectChanges()
  }

  expandAllPivotNodes () {
    this.pivotExpandedKeys.clear()
    this.pivotTree.forEach(n => this.collectExpandableKeys(n))
    this.refreshPivotRows()
    this.cdr.detectChanges()
  }

  collapseAllPivotNodes () {
    this.pivotExpandedKeys.clear()
    this.refreshPivotRows()
    this.cdr.detectChanges()
  }

  private computeCrossTab () {
    this.pivotCrossTab = null
    this.pivotTruncatedColumns = 0
    if (!this.pivotRowField || !this.pivotColField) {
      return
    }

    // Il cross-tab usa la prima misura selezionata.
    const measure = this.pivotMeasures[0] || { key: 'count', label: 'Count', aggregation: 'count' as PivotAgg, valueField: null }
    this.pivotMeasures = [measure]

    const source = this.pivotSourceRows
    const rowMap = new Map<string, any[]>()
    const colMap = new Map<string, any[]>()
    const cellMap = new Map<string, any[]>()
    for (const row of source) {
      const rv = this.groupValue(row, this.pivotRowField)
      const cv = this.groupValue(row, this.pivotColField)
      this.push(rowMap, rv, row)
      this.push(colMap, cv, row)
      this.push(cellMap, rv + '§' + cv, row)
    }

    // Colonne: top-N per frequenza, con eventuale bucket "Others".
    const allColVals = [...colMap.keys()]
    let colVals: string[]
    if (this.pivotTopColumns > 0 && allColVals.length > this.pivotTopColumns) {
      colVals = [...allColVals]
        .sort((a, b) => (colMap.get(b)?.length || 0) - (colMap.get(a)?.length || 0))
        .slice(0, this.pivotTopColumns)
        .sort()
    } else {
      colVals = [...allColVals].sort()
    }
    const othersSet = new Set(allColVals.filter(c => !colVals.includes(c)))
    const hasOthers = this.pivotGroupOthers && othersSet.size > 0
    this.pivotTruncatedColumns = othersSet.size
    const finalCols = hasOthers ? [...colVals, 'Others'] : colVals

    // Righe ordinate per etichetta o per totale.
    const rowTotalOf = new Map<string, number | null>()
    rowMap.forEach((rows, rv) => rowTotalOf.set(rv, this.aggregateFor(rows, measure)))
    let rowVals = [...rowMap.keys()]
    rowVals.sort((a, b) => {
      const cmp = this.pivotSortBy === 'value'
        ? ((rowTotalOf.get(a) ?? 0) - (rowTotalOf.get(b) ?? 0))
        : a.localeCompare(b)
      return this.pivotSortOrder === 'desc' ? -cmp : cmp
    })

    const rowsFor = (rv: string, cv: string): any[] => {
      if (cv === 'Others') {
        const acc: any[] = []
        othersSet.forEach(c => {
          const bucket = cellMap.get(rv + '§' + c)
          if (bucket) {
            acc.push(...bucket)
          }
        })
        return acc
      }
      return cellMap.get(rv + '§' + cv) || []
    }

    const cells = rowVals.map(rv => finalCols.map(cv => this.aggregateFor(rowsFor(rv, cv), measure)))
    const rowTotals = rowVals.map(rv => rowTotalOf.get(rv) ?? null)
    const colTotals = finalCols.map(cv => {
      if (cv === 'Others') {
        const acc: any[] = []
        othersSet.forEach(c => {
          const bucket = colMap.get(c)
          if (bucket) {
            acc.push(...bucket)
          }
        })
        return this.aggregateFor(acc, measure)
      }
      return this.aggregateFor(colMap.get(cv) || [], measure)
    })
    const grandTotal = this.aggregateFor(source, measure)

    this.pivotCrossTab = {
      rowFields: rowVals,
      colFields: finalCols,
      cells,
      rowTotals,
      colTotals,
      grandTotal,
      hasOthers
    }
    this.pivotGrandTotals = [grandTotal]
  }

  // --- Pivot chart (echarts) ---
  togglePivotChart () {
    if (this.pivotShowChart) {
      this.pivotShowChart = false
      this.disposeChart()
      return
    }
    if (!this.hasPivotResult()) {
      return
    }
    this.pivotShowChart = true
    this.buildChart()
    setTimeout(() => this.renderPivotChart(), 50)
  }

  private buildChart () {
    const axisLabel = { formatter: (v: number) => this.compactNumber(v) }
    const valueFormatter = (v: number) => this.formatPivotValue(v)
    if (this.pivotMode === 'grouped') {
      // Grafico sulle foglie (evita il doppio conteggio dei nodi padre).
      const leaves: PivotNode[] = []
      const collect = (nodes: PivotNode[]) => nodes.forEach(n => n.children.length ? collect(n.children) : leaves.push(n))
      collect(this.pivotTree)
      const base = this.pivotGrandTotals[0] ?? 0
      let data = leaves.map(n => ({ label: n.groups.join(' / '), value: n.values[0] ?? 0 }))
      data = data.sort((a, b) => b.value - a.value)
      if (this.pivotChartTopN > 0 && data.length > this.pivotChartTopN) {
        data = data.slice(0, this.pivotChartTopN)
      }
      const labels = data.map(d => d.label)
      const values = data.map(d => this.pivotChartPercent && base ? +(d.value / base * 100).toFixed(2) : d.value)
      const valueName = this.pivotMeasures[0]?.label || 'Value'
      if (this.pivotChartType === 'pie') {
        this.pivotChartOptions = {
          tooltip: { trigger: 'item', valueFormatter },
          legend: { type: 'scroll', bottom: 0 },
          color: this.pivotPalette,
          series: [{
            name: valueName,
            type: 'pie',
            radius: ['35%', '65%'],
            emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' } },
            label: { show: this.pivotChartDataLabels, formatter: '{b}: {d}%' },
            data: labels.map((label, i) => ({ name: label, value: values[i] }))
          }]
        }
      } else {
        const useLog = this.pivotChartLogScale && values.length > 0 && values.every(v => v > 0)
        this.pivotChartOptions = {
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: { type: 'category', data: labels, axisLabel: { interval: 0, rotate: labels.length > 8 ? 45 : 0 } },
          yAxis: { type: useLog ? 'log' : 'value', axisLabel },
          series: [{
            name: valueName,
            type: this.pivotChartType === 'line' ? 'line' : 'bar',
            smooth: true,
            data: values,
            itemStyle: { color: this.pivotPalette[0] },
            barMaxWidth: 40,
            label: { show: this.pivotChartDataLabels, position: 'top', formatter: (p: any) => this.compactNumber(p.value) }
          }]
        }
      }
    } else if (this.pivotCrossTab) {
      const ct = this.pivotCrossTab
      let rowIdx = ct.rowFields.map((_, i) => i)
      if (this.pivotChartTopN > 0 && rowIdx.length > this.pivotChartTopN) {
        rowIdx = rowIdx.sort((a, b) => (ct.rowTotals[b] ?? 0) - (ct.rowTotals[a] ?? 0)).slice(0, this.pivotChartTopN)
      }
      const rowLabels = rowIdx.map(i => ct.rowFields[i])
      const isLine = this.pivotChartType === 'line'
      const series = ct.colFields.map((col, ci) => ({
        name: col,
        type: isLine ? 'line' : 'bar',
        stack: isLine ? undefined : 'total',
        smooth: true,
        itemStyle: { color: this.pivotPalette[ci % this.pivotPalette.length] },
        data: rowIdx.map(ri => ct.cells[ri][ci]),
        label: { show: this.pivotChartDataLabels, position: 'top', formatter: (p: any) => this.compactNumber(p.value) }
      }))
      const allValues = rowIdx.flatMap(ri => ct.colFields.map((_, ci) => ct.cells[ri][ci] ?? 0))
      const useLog = this.pivotChartLogScale && allValues.length > 0 && allValues.every(v => v > 0)
      this.pivotChartOptions = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter },
        legend: { data: ct.colFields, type: 'scroll' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: rowLabels, axisLabel: { interval: 0, rotate: rowLabels.length > 8 ? 45 : 0 } },
        yAxis: { type: useLog ? 'log' : 'value', axisLabel },
        series
      }
    }
  }

  private renderPivotChart () {
    const el = this.pivotChartEl?.nativeElement
    if (!el || !this.pivotChartOptions) {
      return
    }
    if (this.pivotChartInstance) {
      this.pivotChartInstance.dispose()
    }
    this.pivotChartInstance = echarts.init(el)
    this.pivotChartInstance.setOption(this.pivotChartOptions)
    this.pivotChartInstance.resize()
  }

  private disposeChart () {
    if (this.pivotChartInstance) {
      this.pivotChartInstance.dispose()
      this.pivotChartInstance = null
    }
  }

  // Matrice piatta del risultato pivot (intestazioni + righe di celle).
  private buildPivotMatrix (): { headers: string[], rows: any[][] } {
    if (this.pivotMode === 'grouped') {
      const groupFields = this.pivotGroupBy.filter(f => !!f)
      const headers = [...groupFields.map(f => this.headerOf(f)), ...this.pivotMeasures.map(m => m.label)]
      if (this.pivotShowPercentage) {
        headers.push('% of total')
      }
      const base = this.pivotGrandTotals[0]
      const rows = this.pivotResult.map(r => {
        const cells = groupFields.map((_, gi) => r.__groups[gi] ?? '')
        this.pivotMeasures.forEach((_, mi) => cells.push(r.__values[mi]))
        if (this.pivotShowPercentage) {
          cells.push(base ? (((r.__values[0] ?? 0) / base) * 100).toFixed(2) + '%' : '')
        }
        return cells
      })
      return { headers, rows }
    }
    if (this.pivotCrossTab) {
      const ct = this.pivotCrossTab
      const headers = [this.headerOf(this.pivotRowField), ...ct.colFields, 'Total']
      const rows = ct.rowFields.map((rv, ri) => [
        rv,
        ...ct.colFields.map((_, ci) => ct.cells[ri][ci]),
        ct.rowTotals[ri]
      ])
      rows.push(['Total', ...ct.colFields.map((_, ci) => ct.colTotals[ci]), ct.grandTotal])
      return { headers, rows }
    }
    return { headers: [], rows: [] }
  }

  // --- Export pivot result ---
  exportPivot () {
    const { headers, rows } = this.buildPivotMatrix()
    if (!headers.length) {
      return
    }
    const exportName = 'custom_analysis_' + dayjs().format('YYYYMMDD')
    import('exceljs').then(module => {
      const ExcelJS = (module as any).default || module
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Pivot')
      worksheet.columns = headers.map(h => ({ header: h, key: h, width: 24 }))
      rows.forEach(cells => {
        const row: any = {}
        headers.forEach((h, i) => { row[h] = cells[i] })
        worksheet.addRow(row)
      })
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
      worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }
      workbook.xlsx.writeBuffer().then((buffer) => {
        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        FileSaver.saveAs(new Blob([buffer], { type: EXCEL_TYPE }), exportName + '.xlsx')
      })
    })
  }

  exportPivotCsv () {
    const { headers, rows } = this.buildPivotMatrix()
    if (!headers.length) {
      return
    }
    const escape = (value: any): string => {
      const text = value === null || value === undefined ? '' : String(value)
      return /[";\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text
    }
    const lines: string[] = [headers.map(escape).join(';')]
    rows.forEach(cells => lines.push(cells.map(escape).join(';')))
    const csv = '\ufeff' + lines.join('\r\n')
    FileSaver.saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'custom_analysis_' + dayjs().format('YYYYMMDD') + '.csv')
  }

  exportPivotChartPng () {
    if (!this.pivotChartInstance) {
      return
    }
    const url = this.pivotChartInstance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' })
    fetch(url).then(response => response.blob()).then(blob => {
      FileSaver.saveAs(blob, 'custom_analysis_chart_' + dayjs().format('YYYYMMDD') + '.png')
    })
  }

  // --- Drill-down: filtra la tabella principale sul gruppo/cella selezionati ---
  private applyAnalysisFilter (field: string, value: string) {
    if (!field || value === '(empty)') {
      return
    }
    this.pt.filter(value, field, 'equals')
    this.pivotAppliedFilters = [...this.pivotAppliedFilters.filter(f => f.field !== field), { field, value }]
  }

  drillDownPivot (row: any) {
    const groupFields = this.pivotGroupBy.filter(f => !!f)
    groupFields.forEach((field, i) => this.applyAnalysisFilter(field, row.__groups?.[i]))
    if (!this.pivotDrillDownKeepOpen) {
      this.openStats = false
    } else {
      this.cdr.detectChanges()
    }
  }

  drillDownCrossTab (rowVal: string, colVal: string) {
    this.applyAnalysisFilter(this.pivotRowField, rowVal)
    if (colVal !== 'Others') {
      this.applyAnalysisFilter(this.pivotColField, colVal)
    }
    if (!this.pivotDrillDownKeepOpen) {
      this.openStats = false
    } else {
      this.cdr.detectChanges()
    }
  }

  clearAnalysisFilters () {
    if (!this.pt) {
      return
    }
    this.pivotAppliedFilters.forEach(f => this.pt.filter(null, f.field, 'equals'))
    this.pivotAppliedFilters = []
    this.cdr.detectChanges()
  }

  resetPivot () {
    this.clearPivotStorage()
    this.pivotGroupBy = []
    this.pivotRowField = null
    this.pivotColField = null
    this.pivotValueField = null
    this.pivotAggregations = ['count']
    this.pivotMeasure = 'value'
    this.pivotMinValue = null
    this.pivotMaxValue = null
    this.pivotHavingOperator = '>='
    this.pivotSortBy = 'label'
    this.pivotSortOrder = 'asc'
    this.pivotDateBucket = 'none'
    this.pivotNumericInterval = null
    this.pivotResult = []
    this.pivotColumns = []
    this.pivotMeasures = []
    this.pivotGrandTotals = []
    this.pivotCrossTab = null
    this.pivotTruncatedColumns = 0
    this.pivotTree = []
    this.pivotExpandedKeys.clear()
    this.pivotShowChart = false
    this.pivotChartType = 'bar'
    this.disposeChart()
  }

  // --- Pivot configuration persistence ---
  private get pivotStorageKey (): string | null {
    const base = this._settingsKey || this.prevKey
    return base ? `fq-table-pivot:${base}` : null
  }

  private clearPivotStorage () {
    const key = this.pivotStorageKey
    if (key) {
      try {
        localStorage.removeItem(key)
      } catch {
        // localStorage non disponibile
      }
    }
  }

  private persistPivotConfig () {
    const key = this.pivotStorageKey
    if (!key) {
      return
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        mode: this.pivotMode,
        groupBy: this.pivotGroupBy,
        aggregations: this.pivotAggregations,
        valueField: this.pivotValueField,
        sortBy: this.pivotSortBy,
        sortOrder: this.pivotSortOrder,
        havingOperator: this.pivotHavingOperator,
        minValue: this.pivotMinValue,
        maxValue: this.pivotMaxValue,
        rowField: this.pivotRowField,
        colField: this.pivotColField,
        measure: this.pivotMeasure,
        topColumns: this.pivotTopColumns,
        groupOthers: this.pivotGroupOthers,
        dateBucket: this.pivotDateBucket,
        numericInterval: this.pivotNumericInterval,
        useFiltered: this.pivotUseFiltered,
        compactNumbers: this.pivotCompactNumbers,
        keepOpen: this.pivotDrillDownKeepOpen,
        chartType: this.pivotChartType,
        chartTopN: this.pivotChartTopN,
        chartLog: this.pivotChartLogScale,
        chartLabels: this.pivotChartDataLabels,
        chartPercent: this.pivotChartPercent
      }))
    } catch {
      // localStorage non disponibile
    }
  }

  private loadPivotConfig () {
    const key = this.pivotStorageKey
    if (!key) {
      return
    }
    try {
      const raw = localStorage.getItem(key)
      if (!raw) {
        return
      }
      const config = JSON.parse(raw)
      if (config.mode) { this.pivotMode = config.mode }
      if (Array.isArray(config.groupBy)) { this.pivotGroupBy = config.groupBy }
      if (Array.isArray(config.aggregations) && config.aggregations.length) { this.pivotAggregations = config.aggregations }
      if (config.valueField !== undefined) { this.pivotValueField = config.valueField }
      if (config.sortBy) { this.pivotSortBy = config.sortBy }
      if (config.sortOrder) { this.pivotSortOrder = config.sortOrder }
      if (config.havingOperator) { this.pivotHavingOperator = config.havingOperator }
      if (config.minValue !== undefined) { this.pivotMinValue = config.minValue }
      if (config.maxValue !== undefined) { this.pivotMaxValue = config.maxValue }
      if (config.rowField !== undefined) { this.pivotRowField = config.rowField }
      if (config.colField !== undefined) { this.pivotColField = config.colField }
      if (config.measure) { this.pivotMeasure = config.measure }
      if (config.topColumns != null) { this.pivotTopColumns = config.topColumns }
      if (config.groupOthers !== undefined) { this.pivotGroupOthers = config.groupOthers }
      if (config.dateBucket) { this.pivotDateBucket = config.dateBucket }
      if (config.numericInterval !== undefined) { this.pivotNumericInterval = config.numericInterval }
      if (config.useFiltered !== undefined) { this.pivotUseFiltered = config.useFiltered }
      if (config.compactNumbers !== undefined) { this.pivotCompactNumbers = config.compactNumbers }
      if (config.keepOpen !== undefined) { this.pivotDrillDownKeepOpen = config.keepOpen }
      if (config.chartType) { this.pivotChartType = config.chartType }
      if (config.chartTopN != null) { this.pivotChartTopN = config.chartTopN }
      if (config.chartLog !== undefined) { this.pivotChartLogScale = config.chartLog }
      if (config.chartLabels !== undefined) { this.pivotChartDataLabels = config.chartLabels }
      if (config.chartPercent !== undefined) { this.pivotChartPercent = config.chartPercent }
    } catch {
      // config non valida
    }
  }

  private get storageKey (): string | null {
    const base = this._settingsKey || this.prevKey
    if (!base) {
      return null
    }
    const fields = this._columns
      .map(col => col.field)
      .filter(field => !!field)
      .sort()
      .join('|')
    return `fq-table-settings:${base}:${fields}`
  }

  private loadSettings (): FqTableSavedSettings | null {
    const key = this.storageKey
    if (!key) {
      return null
    }
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) as FqTableSavedSettings : null
    } catch {
      return null
    }
  }

  private applySavedSettings (): void {
    const saved = this.savedSettings
    if (!saved) {
      return
    }
    if (Array.isArray(saved.columns) && saved.columns.length) {
      const byField = new Map<string, { field: string, show: boolean, frozen: boolean }>()
      const order = new Map<string, number>()
      saved.columns.forEach((col, index) => {
        byField.set(col.field, col)
        order.set(col.field, index)
      })
      this._columns = this._columns
        .map(col => {
          const preference = byField.get(col.field)
          return preference
            ? { ...col, show: preference.show, frozen: preference.frozen === true }
            : col
        })
        .sort((a, b) => (order.get(a.field) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.field) ?? Number.MAX_SAFE_INTEGER))
    }
    if (saved.groupRowsBy !== undefined) {
      this._groupRowsBy = saved.groupRowsBy
    }
    if (saved.rowGroupMode !== undefined) {
      this._rowGroupMode = saved.rowGroupMode
    }
    if (saved.expandableRows !== undefined) {
      this._expandableRows = saved.expandableRows
    }
    if (saved.showColFilters !== undefined) {
      this.showColFilters = saved.showColFilters
    }
    if (saved.density) {
      this.density = saved.density
      this.previewDensity = saved.density
    }
    if (saved.rows != null) {
      this.rows = saved.rows
    }
    if (saved.sortField !== undefined) {
      this.sortField = saved.sortField
    }
    if (saved.sortOrder != null) {
      this.sortOrder = saved.sortOrder as 1 | -1
    }
  }

  private restoreSettings (): void {
    this.savedSettings = this.loadSettings()
    if (this.savedSettings && this._columns.length) {
      this.applySavedSettings()
      this.columnsCopy = cloneDeep(this._columns)
      this.updateVisibleColumns()
      this.updateRowColumns()
    }
  }

  private persistSettings (columns = this._columns): void {
    const key = this.storageKey
    if (!key) {
      return
    }
    const settings: FqTableSavedSettings = {
      columns: columns.map(col => ({
        field: col.field,
        show: col.show !== false,
        frozen: col.frozen === true
      })),
      groupRowsBy: this._groupRowsBy,
      rowGroupMode: this._rowGroupMode,
      expandableRows: this._expandableRows,
      showColFilters: this.showColFilters,
      density: this.density,
      rows: this.rows,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    }
    this.savedSettings = settings
    try {
      localStorage.setItem(key, JSON.stringify(settings))
    } catch {
      // localStorage non disponibile
    }
  }

  resetTableSettings (): void {
    const key = this.storageKey
    if (key) {
      try {
        localStorage.removeItem(key)
      } catch {
        // localStorage non disponibile
      }
    }
    this.savedSettings = null
  }

  showOptions () {
    this.columnsCopy = cloneDeep(this.columns)
    this.previewDensity = this.density
    this.openOptions = true
  }

  dropColumn (event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return
    }
    moveItemInArray(this.columnsCopy, event.previousIndex, event.currentIndex)
  }

  saveOptions () {
    // Aggiorna i columns originali con i valori della copia
    this.openOptions = false
    this.density = this.previewDensity
    this.persistSettings(this.columnsCopy)
    setTimeout(() => {
      // Il cambio di `columns` (visibilità/ordine) e `density` vengono già
      // riflessi dal template: non serve svuotare `tableData`/`loading`, che
      // faceva comparire il "Caricamento dati..." sotto la tabella.
      this.setColumnsInternal(cloneDeep(this.columnsCopy))
      this.pt?.reset()
      this.cdr.detectChanges()
      if (this.frozenCount > 0) {
        this.frozenSyncKey = ''
        this.syncFrozenColumns()
      }
    }, 0)
  }

  // Ripristina colonne, densità, raggruppamento e filtri ai valori di default,
  // eliminando anche le impostazioni salvate.
  resetOptions () {
    this.resetTableSettings()
    this.openOptions = false
    this.density = 'normal'
    this.previewDensity = 'normal'
    this._groupRowsBy = ''
    this._rowGroupMode = ''
    this._expandableRows = false
    this.expandedRowGroups = {}
    this.showColFilters = false
    this.setColumnsInternal(cloneDeep(this.originalColumns))
    this.columnsCopy = cloneDeep(this._columns)
    setTimeout(() => {
      this.pt?.reset()
      this.cdr.detectChanges()
      if (this.frozenCount > 0) {
        this.frozenSyncKey = ''
        this.syncFrozenColumns()
      }
    }, 0)
  }

  expandAllRows () {
    if (this.expandableRows && this.tableData && this.tableData.length) {
      const allSections = [...new Set(this.tableData.map(x => x[this.groupRowsBy]))]
      allSections.forEach(section => {
        this.expandedRowGroups[section] = true // espande tutto
      })
    }
  }

  get computedGlobalFilterFields (): string[] {
    return this.globalFilterFieldsCache
  }

  get columnsOptions () {
    return this.visibleColumns
  }

  get visibleColumns (): any[] {
    return this.cachedVisibleColumns
  }

  private updateVisibleColumns () {
    // Manteniamo l'ordine originale delle colonne: il freeze viene gestito
    // applicando position:sticky + left alla cella la cui colonna ha frozen===true,
    // esattamente come fa la direttiva pFrozenColumn sull'header. Così header e body
    // restano allineati anche se il body non riordina le colonne congelate.
    this.cachedVisibleColumns = this._columns.filter(col => col.show !== false)
    this.frozenCountCache = this.cachedVisibleColumns.filter(col => col.frozen === true).length
  }

  isColumnVisible (col: any): boolean {
    return this.columns.find(c => c.field === col)?.show
  }

  // La caption va mostrata se almeno un controllo è attivo, non solo per la ricerca.
  // Nota: `showOption`/`showColumnToggle` hanno default true, quindi NON sono usati
  // come trigger per evitare di aggiungere una toolbar a tabelle che non ne avevano.
  get showCaption (): boolean {
    if (this.showToolbar != null) {
      return this.showToolbar
    }
    return this.showSearch || this.showStats || !!this.exportFileName || !!this.captionActionsTemplate
  }

  // --- Default cell formatting (usato dal body di default e dall'export) ---
  formatCell (row: any, col: any): string {
    const value = row ? row[col.field] : null
    if (typeof col.format === 'function') {
      return col.format(value, row)
    }
    if (value === null || value === undefined || value === '') {
      return ''
    }
    switch (col.type) {
      case 'date':
        return dayjs(value).isValid() ? dayjs(value).format(col.dateFormat || 'YYYY-MM-DD') : String(value)
      case 'datetime':
        return dayjs(value).isValid() ? dayjs(value).format(col.dateFormat || 'YYYY-MM-DD HH:mm:ss') : String(value)
      case 'boolean':
        return (value === true || value === 'true' || value === 1 || value === '1')
          ? (col.trueLabel ?? 'Yes')
          : (col.falseLabel ?? 'No')
      case 'number':
        return this.formatNumber(Number(value), col, value)
      case 'currency':
        return this.formatCurrency(Number(value), col, value)
      default:
        return String(value)
    }
  }

  private formatNumber (num: number, col: any, original: any): string {
    if (isNaN(num)) {
      return String(original)
    }
    const decimals = col.decimals ?? 2
    return num.toLocaleString(col.locale || 'it-IT', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
  }

  private formatCurrency (num: number, col: any, original: any): string {
    if (isNaN(num)) {
      return String(original)
    }
    const decimals = col.decimals ?? 2
    return num.toLocaleString(col.locale || 'it-IT', {
      style: 'currency',
      currency: col.currency || 'EUR',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
  }

  // --- Column filters ---
  private static readonly BOOLEAN_FILTER_OPTIONS = [{ label: 'Yes', value: true }, { label: 'No', value: false }]
  private filterOptionsCache = new Map<string, Array<{ label: string, value: any }>>()

  getFilterOptions (col: any): Array<{ label: string, value: any }> {
    if (Array.isArray(col.filterOptions) && col.filterOptions.length) {
      return col.filterOptions
    }
    if (col.filterType === 'boolean') {
      return TableComponent.BOOLEAN_FILTER_OPTIONS
    }
    const cached = this.filterOptionsCache.get(col.field)
    if (cached) {
      return cached
    }
    // Deriva i valori distinti dai dati (max 100) per un filtro a tendina immediato.
    const seen = new Set<string>()
    const options: Array<{ label: string, value: any }> = []
    for (const row of this.tableData || []) {
      const value = row ? row[col.field] : null
      if (value === null || value === undefined || value === '') {
        continue
      }
      const key = String(value)
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      options.push({ label: key, value })
      if (options.length >= 100) {
        break
      }
    }
    options.sort((a, b) => a.label.localeCompare(b.label))
    this.filterOptionsCache.set(col.field, options)
    return options
  }

  getFilterValue (field: string): any {
    return (this.pt?.filters as any)?.[field]?.value ?? null
  }

  getDateFilterValue (field: string): Date | null {
    const value = this.getFilterValue(field)
    if (!value) {
      return null
    }
    const date = value instanceof Date ? value : new Date(value)
    return isNaN(date.getTime()) ? null : date
  }

  private defaultMatchMode (col: any): string {
    if (col.filterMatchMode) {
      return col.filterMatchMode
    }
    if (col.filterType === 'numeric') {
      return 'equals'
    }
    if (col.filterType === 'date') {
      return 'dateIs'
    }
    return 'contains'
  }

  applyColumnFilter (col: any, value: any) {
    if (!this.pt) {
      return
    }
    const mode = this.defaultMatchMode(col)
    const effective = (value === undefined || value === null || value === '') ? null : value
    this.pt.filter(effective, col.field, mode)
    this.pt.resetScrollTop()
    this.filterChange.emit({ field: col.field, value: effective, matchMode: mode })
  }

  clearColumnFilter (col: any) {
    this.applyColumnFilter(col, null)
  }

  getCount (pt: Table): string {
    if (!pt) {
      return 'Showing 0 of 0 items'
    }
    let count = 0
    if (pt.isEmpty()) {
      count = 0
    } else if (pt.hasFilter() && !pt.isEmpty() && pt.filteredValue?.length) {
      count = pt.filteredValue?.length
    } else if (pt.hasFilter() && !pt.isEmpty()) {
      count = pt.value?.length ?? 0
    } else {
      count = pt.value?.length ?? 0
    }
    return `Showing ${count} of ${pt.value?.length ?? 0} items`
  }

  get isTableFiltered (): boolean {
    if (!this.pt || !this.pt.filters) {
      return false
    }
    const filters = this.pt.filters as any
    if (filters.global && filters.global.value) {
      return true
    }
    for (const key of Object.keys(filters)) {
      if (key === 'global') {
        continue
      }
      const f = filters[key]
      if (f && f.value !== null && f.value !== undefined && f.value !== '') {
        return true
      }
    }
    return false
  }

  /** Righe effettivamente visibili dopo filtri globali o di colonna. */
  get filteredCount (): number {
    if (!this.pt) {
      return 0
    }
    return this.pt.filteredValue?.length ?? this.pt.value?.length ?? 0
  }

  clearAllFilters () {
    if (!this.pt) {
      return
    }
    this.pt.filterGlobal(null, 'contains')
    for (const col of this.columns) {
      this.pt.filter(null, col.field, 'contains')
    }
    this.searchTerm = null
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = ''
    }
    this.pt.resetScrollTop()
    this.cdr.detectChanges()
  }

  // Costruisce colonne e righe da esportare: opzionalmente solo le righe
  // filtrate e solo le colonne visibili, con valori formattati/derivati.
  private buildExportData (filteredOnly = this.exportFilteredOnly): { columns: any[], rows: any[] } {
    const columns = (this.exportHiddenColumns ? this._columns : this.cachedVisibleColumns)
      .filter(col => !!col.field && col.exportable !== false)
    const hasFilter = this.pt?.hasFilter ? this.pt.hasFilter() : false
    const source = (filteredOnly && hasFilter)
      ? (this.pt?.filteredValue || [])
      : this.tableData
    const rows = (source || []).map(item => {
      const mapped: any = {}
      columns.forEach(col => {
        let value = item[col.field]
        if (typeof col.exportValue === 'function') {
          value = col.exportValue(value, item)
        } else if (col.field === 'Tecnologia' && this.useTecnologiaAgglomerataExport) {
          value = this.getTecnologiaExport(item)
        } else if (typeof col.format === 'function') {
          value = col.format(value, item)
        } else if (col.type) {
          value = this.formatCell(item, col)
        }
        mapped[col.header || col.field] = value
      })
      return mapped
    })
    return { columns, rows }
  }

  exportExcel (filteredOnly = this.exportFilteredOnly) {
    const { columns, rows } = this.buildExportData(filteredOnly)

    // inserire i dati in una tabella excel con ExcelJS per supportare gli stili
    import('exceljs').then(module => {
      const ExcelJS = (module as any).default || module
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Data')

      // Aggiungere le colonne con gli header
      worksheet.columns = columns.map(col => ({
        header: col.header || col.field,
        key: col.header || col.field,
        width: Math.min((col.header || col.field).length + 5, 50)
      }))

      // Aggiungere i dati
      rows.forEach(row => {
        worksheet.addRow(row)
      })

      // Stile dell'header
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      }
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }

      // Centrare tutte le celle dei dati
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { // salta l'header
          row.eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center' }
          })
        }
      })

      // Aggiungere auto-filtro
      if (columns.length) {
        worksheet.autoFilter = {
          from: { row: 1, column: 1 },
          to: { row: rows.length + 1, column: columns.length }
        }
      }

      // Esportare il file
      workbook.xlsx.writeBuffer().then((buffer) => {
        this.saveAsExcelFile(buffer, this.exportFileName)
      })
    })
  }

  exportCsv (filteredOnly = this.exportFilteredOnly) {
    const { columns, rows } = this.buildExportData(filteredOnly)
    const headers = columns.map(col => col.header || col.field)
    const separator = ';'
    const escape = (value: any): string => {
      const text = value === null || value === undefined ? '' : String(value)
      return /[";\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text
    }
    const lines: string[] = [headers.map(escape).join(separator)]
    rows.forEach(row => {
      lines.push(headers.map(header => escape(row[header])).join(separator))
    })
    // BOM iniziale per la corretta lettura degli accenti in Excel
    const csv = '\ufeff' + lines.join('\r\n')
    const data = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(data, (this.exportFileName || 'export') + '_export_' + dayjs().format('YYYYMMDD') + '.csv')
  }

  // --- Export scope confirmation (filtered vs all) ---
  get exportTotalCount (): number {
    return this.pt?.value?.length ?? this.tableData?.length ?? 0
  }

  get exportFilteredCount (): number {
    if (this.pt?.hasFilter?.()) {
      return this.pt?.filteredValue?.length ?? 0
    }
    return this.exportTotalCount
  }

  get exportHasFilteredScope (): boolean {
    return this.exportFilteredCount < this.exportTotalCount
  }

  // Entry point of the toolbar export buttons: ask the scope only when the table
  // is actually filtered and the count differs.
  requestExport (format: 'excel' | 'csv') {
    if (this.confirmExportWhenFiltered && this.exportHasFilteredScope) {
      this.pendingExportFormat = format
      this.exportDialogVisible = true
      return
    }
    this.runExport(format, this.exportFilteredOnly)
  }

  private runExport (format: 'excel' | 'csv', filteredOnly: boolean) {
    if (format === 'csv') {
      this.exportCsv(filteredOnly)
    } else {
      this.exportExcel(filteredOnly)
    }
  }

  confirmExportFilteredOnly () {
    this.exportDialogVisible = false
    this.runExport(this.pendingExportFormat, true)
  }

  confirmExportAll () {
    this.exportDialogVisible = false
    this.runExport(this.pendingExportFormat, false)
  }


  getTecnologiaExport (row: any): string {
    const chips: string[] = []
    if (row.Tecnologia === 'FORTINET') {
      chips.push('FORTINET')
    }
    if (row.Tecnologia === 'CISCO') {
      chips.push('CISCO')
    }
    if (row.MigrazioneSDWAN && row.MigrazioneNRD) {
      chips.push('NRD')
    } else if (row.MigrazioneSDWAN) {
      chips.push('SDWAN')
    }
    return chips.join(', ')
  }

  saveAsExcelFile (buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    const EXCEL_EXTENSION = '.xlsx'
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    })
    FileSaver.saveAs(data, fileName + '_export_' + dayjs().format('YYYYMMDD') + EXCEL_EXTENSION)
  }

  showTableStats () {
    this.tableStatistics = this.calculateTableStatistics()
    this.loadPivotConfig()
    this.statsView = 'overview'
    this.openStats = true
  }

  private calculateTableStatistics (): any {
    if (!this.tableData || this.tableData.length === 0) {
      return null
    }

    const stats: any = {
      totalRows: this.tableData.length,
      columns: {}
    }

    // Analizza ogni colonna
    this.columns.forEach(col => {
      const fieldValues = this.tableData.map(row => row[col.field])
      const nonNullValues = fieldValues.filter(v => v !== null && v !== undefined && v !== '')
      const nullCount = fieldValues.length - nonNullValues.length

      const colStats: any = {
        field: col.field,
        header: col.header,
        totalRows: fieldValues.length,
        nullCount,
        nullPercentage: ((nullCount / fieldValues.length) * 100).toFixed(2),
        uniqueCount: new Set(nonNullValues.map(v => String(v))).size
      }

      // Rileva tipo di dato
      const sampleValue = nonNullValues[0]
      const isNumeric = !isNaN(parseFloat(String(sampleValue)))
      const isBoolean = typeof sampleValue === 'boolean'
      const isDate = /^\d{4}-\d{2}-\d{2}/.test(String(sampleValue))

      if (isNumeric && nonNullValues.length > 0 && !isDate) {
        const numericValues = nonNullValues.map(v => parseFloat(String(v))).filter(v => !isNaN(v))
        colStats.type = 'number'
        colStats.min = Math.min(...numericValues)
        colStats.max = Math.max(...numericValues)
        colStats.avg = (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2)
        colStats.sum = numericValues.reduce((a, b) => a + b, 0).toFixed(2)
      } else if (isBoolean) {
        colStats.type = 'boolean'
        const trueCount = nonNullValues.filter(v => v === true || v === 'true' || v === 1 || v === '1').length
        colStats.trueCount = trueCount
        colStats.falseCount = nonNullValues.length - trueCount
      } else if (isDate) {
        colStats.type = 'date'
        const dateValues = nonNullValues
          .map(v => dayjs(String(v), 'YYYY-MM-DD').toDate())
          .filter(d => !isNaN(d.getTime()))
        if (dateValues.length > 0) {
          const times = dateValues.map(d => d.getTime())
          const minDate = new Date(Math.min(...times))
          const maxDate = new Date(Math.max(...times))
          const daysDiff = Math.floor((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24))

          colStats.min = dayjs(minDate).format('YYYY-MM-DD')
          colStats.max = dayjs(maxDate).format('YYYY-MM-DD')
          colStats.dateRangeDays = daysDiff

          // Conteggio per mese
          const monthCount: any = {}
          dateValues.forEach(d => {
            const momentDate = dayjs(d)
            const monthKey = momentDate.format('YYYY-MM')
            monthCount[monthKey] = (monthCount[monthKey] || 0) + 1
          })
          colStats.countByMonth = monthCount
        }
      } else {
        colStats.type = 'string'
        const stringValues = nonNullValues.map(v => String(v))
        colStats.minLength = Math.min(...stringValues.map(v => v.length))
        colStats.maxLength = Math.max(...stringValues.map(v => v.length))
        colStats.avgLength = (stringValues.reduce((sum, v) => sum + v.length, 0) / stringValues.length).toFixed(2)
      }

      // Top 5 valori più frequenti
      const valueCounts: any = {}
      nonNullValues.forEach(v => {
        const key = String(v)
        valueCounts[key] = (valueCounts[key] || 0) + 1
      })

      colStats.topValues = Object.entries(valueCounts)
        .map(([value, count]: [string, any]) => ({
          value,
          count,
          percentage: ((count / nonNullValues.length) * 100).toFixed(2)
        }))
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, 5)

      stats.columns[col.field] = colStats
    })

    return stats
  }
}
@NgModule({
  imports: [CommonModule, SelectModule, MultiSelectModule, SelectButtonModule, DatePickerModule, FormsModule, TableModule, InputGroupModule, InputGroupAddonModule, ButtonModule, DialogModule, ToggleSwitchModule, CheckboxModule, RippleModule, InputTextModule, TableComponent],
  exports: [TableComponent]
})
export class FuseqTableComponent { }
